

import { FONT_MONO } from 'styles/tokens/typography';
import React, { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  IconButton,
  InputAdornment,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tooltip
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { Visibility, VisibilityOff, Add, Delete, ContentCopy, Extension, Edit, Share, Webhook, Lock, InfoOutlined, Chat } from '@mui/icons-material';
import WhatsAppSettingsPanel from './WhatsAppSettingsPanel';
// import axios from 'axios';
import { useAuth } from '../../auth/Auth';
import { useModuleEntitlements } from 'hooks/useModuleEntitlements';

function IntegrationsSettings({
  onSave,
  isSaving,
  companyInfo,
  onDataChange,
  selectedSection: selectedSectionProp,
  fullPageLayout = false,
}) {
  const { user, axiosInstance } = useAuth();
  const { isModuleEnabled, loading: entitlementsLoading } = useModuleEntitlements();
  const whatsappEnabled = entitlementsLoading || isModuleEnabled('WHATSAPP');

  // Check if we're in playground mode
  const isPlayground = false;

  const [showGoogleMapsApiKey, setShowGoogleMapsApiKey] = useState(false);
  const [showAiApiKey, setShowAiApiKey] = useState(false);
  const [showGstApiKey, setShowGstApiKey] = useState(false);
  const [googleMapsApiKey, setGoogleMapsApiKey] = useState('');
  const [aiApiKey, setAiApiKey] = useState('');
  const [gstApiKey, setGstApiKey] = useState('');


  // Webhook configuration states - with dummy data for playground
  const [webhooks, setWebhooks] = useState(isPlayground ? [
    {
      id: 1,
      name: 'Lead Capture Webhook',
      url: 'https://api.example.com/webhooks/lead-capture',
      method: 'POST',
      direction: 'outbound',
      description: 'Sends new lead data to external CRM',
      isActive: true,
      module: 'leads',
      selected_fields: ['name', 'email', 'phone'],
    }
  ] : []);

  const [newWebhook, setNewWebhook] = useState({
    name: '',
    url: '',
    method: 'GET',
    direction: 'outbound',
    description: '',
    isActive: true,
    module: '',
    selected_fields: [],
  });

  const [isLoading, setIsLoading] = useState(true);
  const [notification, setNotification] = useState({ open: false, message: '', type: 'success' });
  const [availableModules, setAvailableModules] = useState(['leads', 'accounts', 'contacts', 'deals']);
  const [availableFields, setAvailableFields] = useState({});
  const [latestByToken, setLatestByToken] = useState({});
  const [isWebhookDialogOpen, setIsWebhookDialogOpen] = useState(false);
  const [isDialogSaving, setIsDialogSaving] = useState(false);
  const [isGeneratingInbound, setIsGeneratingInbound] = useState(false);
  const [isFieldsLoading, setIsFieldsLoading] = useState(false);
  // Section selection — controlled on the full page, internal in legacy dialog use.
  const [selectedSectionInternal, setSelectedSectionInternal] = useState('webhook');
  const selectedSection = selectedSectionProp ?? selectedSectionInternal;
  const setSelectedSection = selectedSectionProp !== undefined
    ? () => {}
    : setSelectedSectionInternal;

  // Placeholder values for empty fields
  const placeholders = {
    googleMapsApiKey: process.env.REACT_APP_EXAMPLE_GOOGLE_MAPS_API_KEY || 'YOUR_GOOGLE_MAPS_API_KEY',
    aiApiKey: process.env.REACT_APP_EXAMPLE_AI_API_KEY || 'YOUR_AI_API_KEY',
    gstApiKey: process.env.REACT_APP_EXAMPLE_GST_API_KEY || 'YOUR_GST_API_KEY'
  };

  // Always resolve backend base URL from axios baseURL when possible
  const getBackendBaseUrl = () => {
    const baseFromAxios = axiosInstance?.defaults?.baseURL;
    if (baseFromAxios) return baseFromAxios.replace(/\/$/, '');

    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    if (hostname.includes('.test-PearlCrm.com')) return `${protocol}//api.test-PearlCrm.com`;
    if (hostname.includes('.PearlCrm.com')) return `${protocol}//api.PearlCrm.com`;
    return (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/$/, '');
  };

  // Function to refresh webhook list from backend
  const refreshWebhookList = async () => {
    if (isPlayground) return; // Don't fetch in playground mode

    try {
      console.log('Refreshing webhook list from backend...');
      // Load saved webhook configs
      const res = await axiosInstance.get('/webhook/configs');
      const saved = Array.isArray(res.data) ? res.data : [];
      console.log('Loaded webhook configs from backend:', saved);

      // Convert saved webhooks to frontend format
      const formattedWebhooks = saved.map(s => {
        let webhookUrl = s.url;

        if (s.direction === 'inbound' && s.url && s.url.startsWith('/webhook/inbound/')) {
          const backendUrl = getBackendBaseUrl();
          webhookUrl = `${backendUrl}${s.url}`;
          console.log(`Converting relative URL ${s.url} to full URL: ${webhookUrl}`);
        }

        return {
          id: s.id || Date.now(),
          name: s.name || '',
          url: webhookUrl,
          method: s.method || (s.direction === 'inbound' ? 'POST' : 'GET'),
          direction: s.direction,
          description: '',
          isActive: s.is_active !== false,
          module: s.module || '',
          selected_fields: s.selected_fields || [],
          token: s.token,
        };
      });

      console.log('Formatted webhooks for frontend:', formattedWebhooks);
      setWebhooks(formattedWebhooks);
    } catch (e) {
      console.error('Error refreshing webhook list:', e);
    }
  };



  useEffect(() => {
    const fetchCompanyInfo = async () => {
      setIsLoading(true);

      if (isPlayground) {
        // Don't fetch real data in playground mode
        setIsLoading(false);
        return;
      }

      try {
        const response = await axiosInstance.get('/company-api-keys/info');
        if (response.data) {
          if (response.data.maps_api_key) setGoogleMapsApiKey(response.data.maps_api_key);
          if (response.data.ai_api_key) setAiApiKey(response.data.ai_api_key);
          if (response.data.gst_api_key) setGstApiKey(response.data.gst_api_key);
        }
      } catch (error) {
        console.error("Error fetching company info:", error);
        setNotification({ open: true, message: "Failed to load company information", type: 'error' });
      } finally {
        setIsLoading(false);
      }
    };

    const loadInitialData = async () => {
      await fetchCompanyInfo();
      if (!isPlayground) {
        try {
          const objs = await axiosInstance.get('/objects');
          // Normalize modules - extract names if they're objects, otherwise use as-is
          const modules = (objs.data || []).map(m => typeof m === 'string' ? m : (m.name || m.id || m));
          setAvailableModules(modules);
        } catch (e) { }
        await refreshWebhookList();
      }
    };

    loadInitialData();
  }, [isPlayground]);

  // When module changes (in inbound mode), fetch its fields
  useEffect(() => {
    if (newWebhook.direction === 'inbound' && newWebhook.module && !isPlayground) {
      (async () => {
        setIsFieldsLoading(true);
        try {
          const res = await axiosInstance.get(`/fields/${newWebhook.module}`);
          setAvailableFields(res.data || {});
        } catch (e) {
          setAvailableFields({});
        } finally {
          setIsFieldsLoading(false);
        }
      })();
    } else if (isPlayground && newWebhook.module) {
      // Set dummy fields for playground
      setAvailableFields({
        name: { label: 'Name', type: 'string' },
        email: { label: 'Email', type: 'string' },
        phone: { label: 'Phone', type: 'string' },
        company: { label: 'Company', type: 'string' },
        source: { label: 'Source', type: 'string' },
        status: { label: 'Status', type: 'string' }
      });
    } else {
      setAvailableFields({});
    }
  }, [newWebhook.direction, newWebhook.module, isPlayground]);

  // Notify parent of data changes
  useEffect(() => {
    if (onDataChange && !isPlayground) {
      onDataChange({
        googleMapsApiKey,
        aiApiKey,
        gstApiKey,
        webhooks
      });
    }
  }, [googleMapsApiKey, aiApiKey, gstApiKey, webhooks, onDataChange, isPlayground]);

  const handleAddWebhook = async () => {
    if (isPlayground) {
      setNotification({
        open: true,
        message: "Cannot add webhooks in playground mode",
        type: 'warning'
      });
      return;
    }

    setIsDialogSaving(true);
    if (!newWebhook.name || !newWebhook.url) {
      setNotification({
        open: true,
        message: "Webhook name and URL are required",
        type: 'error'
      });
      setIsDialogSaving(false);
      return;
    }

    if (!newWebhook.url.startsWith('http://') && !newWebhook.url.startsWith('https://')) {
      setNotification({
        open: true,
        message: "URL must start with http:// or https://",
        type: 'error'
      });
      return;
    }

    // Check if we're editing an existing webhook
    const isEditing = newWebhook.id && webhooks.find(w => w.id === newWebhook.id);

    // Persist to backend store
    try {
      if (isEditing) {
        await axiosInstance.put(`/webhook/config/${newWebhook.id}`, {
          name: newWebhook.name,
          url: newWebhook.url,
          method: newWebhook.method,
          description: newWebhook.description,
          is_active: newWebhook.isActive,
          module: newWebhook.module,
          selected_fields: newWebhook.selected_fields,
        });
      } else {
        if (newWebhook.direction === 'outbound') {
          await axiosInstance.post('/webhook/outbound/save', {
            name: newWebhook.name,
            url: newWebhook.url,
            method: newWebhook.method,
          });
        }
      }

      await refreshWebhookList();

      setNotification({
        open: true,
        message: isEditing ? "Webhook updated successfully" : "Webhook added successfully",
        type: 'success'
      });
    } catch (e) {
      console.error('Error saving webhook to backend:', e);
      await refreshWebhookList();
      setNotification({
        open: true,
        message: 'Webhook saved locally but may not be synced with backend',
        type: 'warning'
      });
    }

    setNewWebhook({
      name: '',
      url: '',
      method: 'GET',
      direction: 'outbound',
      description: '',
      isActive: true,
      module: '',
      selected_fields: [],
      token: undefined,
    });
    setIsWebhookDialogOpen(false);
    setIsDialogSaving(false);
  };

  const handleRemoveWebhook = async (webhookId) => {
    if (isPlayground) {
      setNotification({
        open: true,
        message: "Cannot remove webhooks in playground mode",
        type: 'warning'
      });
      return;
    }

    const webhookToDelete = webhooks.find(w => w.id === webhookId);
    if (!webhookToDelete) return;

    try {
      if (webhookToDelete.token) {
        await axiosInstance.delete(`/webhook/inbound/${webhookToDelete.token}`);
      } else if (webhookToDelete.direction === 'outbound') {
        await axiosInstance.delete('/webhook/outbound/delete', {
          params: { url: webhookToDelete.url }
        });
      } else if (webhookToDelete.id) {
        await axiosInstance.delete(`/webhook/config/${webhookToDelete.id}`);
      }

      await refreshWebhookList();

      setNotification({
        open: true,
        message: 'Webhook deleted successfully',
        type: 'success'
      });
    } catch (error) {
      console.error('Error deleting webhook from backend:', error);
      await refreshWebhookList();
      setNotification({
        open: true,
        message: 'Webhook removed locally but failed to delete from backend',
        type: 'warning'
      });
    }
  };

  const handleToggleWebhook = async (webhookId) => {
    if (isPlayground) {
      setNotification({
        open: true,
        message: "Cannot toggle webhooks in playground mode",
        type: 'warning'
      });
      return;
    }

    const webhook = webhooks.find(w => w.id === webhookId);
    if (!webhook) return;

    try {
      await axiosInstance.put(`/webhook/config/${webhookId}`, {
        is_active: !webhook.isActive
      });

      await refreshWebhookList();

      setNotification({
        open: true,
        message: `Webhook ${!webhook.isActive ? 'activated' : 'deactivated'} successfully`,
        type: 'success'
      });
    } catch (error) {
      console.error('Error toggling webhook status:', error);
      setNotification({
        open: true,
        message: 'Failed to update webhook status',
        type: 'error'
      });
    }
  };

  const handleEditWebhook = (webhook) => {
    if (isPlayground) {
      setNotification({
        open: true,
        message: "Cannot edit webhooks in playground mode",
        type: 'warning'
      });
      return;
    }

    setNewWebhook({
      ...webhook,
      name: webhook.name || '',
      url: webhook.url || '',
      method: webhook.method || 'GET',
      direction: webhook.direction || 'outbound',
      description: webhook.description || '',
      isActive: webhook.isActive !== false,
      module: webhook.module || '',
      selected_fields: webhook.selected_fields || [],
      token: webhook.token,
    });
    setIsWebhookDialogOpen(true);
  };

  const handleShareWebhook = (webhook) => {
    let textContent = `Webhook Configuration
====================

Webhook Name: ${webhook.name}
Method: ${webhook.method}
URL: ${webhook.url}
Description: ${webhook.description || 'No description'}
Is Active: ${webhook.isActive ? 'Yes' : 'No'}`;

    if (webhook.direction === 'inbound') {
      textContent += `
Module: ${webhook.module || 'Not specified'}
Selected Fields: ${webhook.selected_fields && webhook.selected_fields.length > 0 ? webhook.selected_fields.join(', ') : 'None'}`;

      if (webhook.selected_fields && webhook.selected_fields.length > 0) {
        const sampleJson = webhook.selected_fields.reduce((acc, key) => ({ ...acc, [key]: "<value>" }), {});
        textContent += `

Sample JSON:
${JSON.stringify(sampleJson, null, 2)}`;
      }
    }

    textContent += `

Generated on: ${new Date().toLocaleString()}`;

    const dataBlob = new Blob([textContent], { type: 'text/plain' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${webhook.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_webhook_info.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setNotification({
      open: true,
      message: 'Webhook information downloaded successfully',
      type: 'success'
    });
  };

  const handleTestWebhook = async (webhook) => {
    if (isPlayground) {
      setNotification({
        open: true,
        message: "Cannot test webhooks in playground mode",
        type: 'warning'
      });
      return;
    }

    try {
      const testFieldMapping = {
        "id": "leadid",
        "name": "name",
        "status": "status",
        "email": "email",
        "phone": "phone",
        "company": "company"
      };

      const response = await axiosInstance.get('/webhook/test', {
        params: {
          url: webhook.url,
          method: webhook.method,
          params: {},
          field_mapping: JSON.stringify(testFieldMapping),
          custom_params: JSON.stringify({})
        }
      });

      if (response.data.success) {
        setNotification({
          open: true,
          message: `Webhook test successful for ${webhook.name}`,
          type: 'success'
        });
      } else {
        setNotification({
          open: true,
          message: `Webhook test failed for ${webhook.name}: ${response.data.response_text}`,
          type: 'error'
        });
      }
    } catch (error) {
      setNotification({
        open: true,
        message: `Webhook test failed for ${webhook.name}: ${error.response?.data?.detail || error.message}`,
        type: 'error'
      });
    }
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const handleCopyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setNotification({
        open: true,
        message: `${label} copied to clipboard`,
        type: 'success'
      });
    } catch (error) {
      setNotification({
        open: true,
        message: `Failed to copy ${label}`,
        type: 'error'
      });
    }
  };



  if (isLoading) {
    return <MDBox p={3}>Loading company information...</MDBox>;
  }

  return (
    <MDBox p={fullPageLayout ? 0 : 3}>
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{ zIndex: 9999 }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.type}>
          {notification.message}
        </Alert>
      </Snackbar>

      {/* Playground Mode Banner */}
      {isPlayground && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          icon={<InfoOutlined />}
        >
          <MDTypography variant="caption" fontWeight="medium">
            Playground Mode - This is a read-only view. All fields show example values to demonstrate the integration setup.
          </MDTypography>
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Section Cards — hidden on full page (tabs live in IntegrationsSettingsPage) */}
        {!fullPageLayout && (
        <Grid item xs={12}>
          <Grid container spacing={2} alignItems="stretch">
            <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
              <Card
                onClick={() => setSelectedSection('webhook')}
                role="button"
                tabIndex={0}
                sx={{
                  cursor: 'pointer',
                  border: selectedSection === 'webhook' ? '2px solid #1a73e8' : '1px solid #e0e0e0',
                  borderRadius: 2,
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.2s ease-in-out',
                  boxShadow: selectedSection === 'webhook' ? '0 6px 16px rgba(26,115,232,0.15)' : '0 2px 8px rgba(0,0,0,0.06)',
                  '&:hover': {
                    boxShadow: '0 8px 18px rgba(0,0,0,0.12)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Webhook color="primary" />
                    <Typography variant="h6">Webhooks</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Configure inbound/outbound webhook integrations
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
              <Card
                onClick={() => setSelectedSection('api')}
                role="button"
                tabIndex={0}
                sx={{
                  cursor: 'pointer',
                  border: selectedSection === 'api' ? '2px solid #1a73e8' : '1px solid #e0e0e0',
                  borderRadius: 2,
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.2s ease-in-out',
                  boxShadow: selectedSection === 'api' ? '0 6px 16px rgba(26,115,232,0.15)' : '0 2px 8px rgba(0,0,0,0.06)',
                  '&:hover': {
                    boxShadow: '0 8px 18px rgba(0,0,0,0.12)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Share color="primary" />
                    <Typography variant="h6">API Integrations</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Configure Google Maps, AI and GST APIs
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
              <Card
                onClick={() => setSelectedSection('social')}
                role="button"
                tabIndex={0}
                sx={{
                  cursor: 'pointer',
                  border: selectedSection === 'social' ? '2px solid #1a73e8' : '1px solid #e0e0e0',
                  borderRadius: 2,
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.2s ease-in-out',
                  opacity: whatsappEnabled ? 1 : 0.85,
                  boxShadow: selectedSection === 'social' ? '0 6px 16px rgba(26,115,232,0.15)' : '0 2px 8px rgba(0,0,0,0.06)',
                  '&:hover': {
                    boxShadow: '0 8px 18px rgba(0,0,0,0.12)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chat color="primary" />
                    <Typography variant="h6">Social</Typography>
                    {!whatsappEnabled && (
                      <Lock fontSize="small" sx={{ color: 'warning.main' }} />
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Configure WhatsApp Business messaging
                  </Typography>
                  {!whatsappEnabled && (
                    <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
                      WhatsApp add-on required
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
        )}

        {/* Social Section */}
        {selectedSection === 'social' && (
          <Grid item xs={12}>
            <WhatsAppSettingsPanel isPlayground={isPlayground} />
          </Grid>
        )}

        {/* API Section */}
        {selectedSection === 'api' && (
          <>
            {/* Google Maps Integration */}
            <Grid item xs={12}>
              <MDTypography variant="h6" fontWeight="medium">
                Google Maps Integration
              </MDTypography>
              <MDTypography variant="body2" color="text">
                Configure your Google Maps API key for location-based features
              </MDTypography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Google Maps API Key"
                value={googleMapsApiKey || ''}
                onChange={(e) => !isPlayground && setGoogleMapsApiKey(e.target.value)}
                type={showGoogleMapsApiKey ? 'text' : 'password'}
                disabled={isPlayground}
                placeholder={placeholders.googleMapsApiKey}
                helperText={isPlayground && !googleMapsApiKey ? "Example: " + placeholders.googleMapsApiKey : ""}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {!isPlayground && (
                        <IconButton
                          onClick={() => setShowGoogleMapsApiKey(!showGoogleMapsApiKey)}
                          edge="end"
                        >
                          {showGoogleMapsApiKey ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      )}
                      {isPlayground && <Lock fontSize="small" color="action" />}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* AI Integration */}
            <Grid item xs={12} mt={2}>
              <MDTypography variant="h6" fontWeight="medium">
                AI Integration
              </MDTypography>
              <MDTypography variant="body2" color="text">
                Configure your AI API key for AI-powered features
              </MDTypography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="AI API Key"
                value={aiApiKey || ''}
                onChange={(e) => !isPlayground && setAiApiKey(e.target.value)}
                type={showAiApiKey ? 'text' : 'password'}
                disabled={isPlayground}
                placeholder={placeholders.aiApiKey}
                helperText={isPlayground && !aiApiKey ? "Example: " + placeholders.aiApiKey : ""}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {!isPlayground && (
                        <IconButton
                          onClick={() => setShowAiApiKey(!showAiApiKey)}
                          edge="end"
                        >
                          {showAiApiKey ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      )}
                      {isPlayground && <Lock fontSize="small" color="action" />}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* GST Integration */}
            <Grid item xs={12} mt={2}>
              <MDTypography variant="h6" fontWeight="medium">
                GST Integration
              </MDTypography>
              <MDTypography variant="body2" color="text">
                Configure your GST API key for GST-related features
              </MDTypography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="GST API Key"
                value={gstApiKey || ''}
                onChange={(e) => !isPlayground && setGstApiKey(e.target.value)}
                type={showGstApiKey ? 'text' : 'password'}
                disabled={isPlayground}
                placeholder={placeholders.gstApiKey}
                helperText={isPlayground && !gstApiKey ? "Example: " + placeholders.gstApiKey : ""}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {!isPlayground && (
                        <IconButton
                          onClick={() => setShowGstApiKey(!showGstApiKey)}
                          edge="end"
                        >
                          {showGstApiKey ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      )}
                      {isPlayground && <Lock fontSize="small" color="action" />}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </>
        )}

        {selectedSection === 'webhook' && (
          <>
            {/* Webhook Integration */}
            <Grid item xs={12} mt={3}>
              <Divider sx={{ mb: 2 }} />
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <MDTypography variant="h6" fontWeight="medium">
                  Webhook Integrations
                </MDTypography>
              </Box>
              <MDTypography variant="body2" color="text" sx={{ mb: 2 }}>
                Configure webhook endpoints for third-party integrations. These webhooks will be available for use in automations. GET is recommended for simple notifications, POST for sending data.
              </MDTypography>
            </Grid>

            {/* Webhook Configuration Dialog */}
            <Dialog open={isWebhookDialogOpen} onClose={() => setIsWebhookDialogOpen(false)} maxWidth="md" fullWidth>
              <DialogTitle>
                {newWebhook.id ? 'Edit Webhook' : 'Configure Webhook'}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {newWebhook.direction === 'inbound'
                    ? <>Inbound: Receive data at a URL we generate for you and auto-create records based on selected fields.</>
                    : <>Outbound: Send data to an external URL.</>
                  }
                </Typography>
              </DialogTitle>
              <DialogContent dividers>
                <Grid container spacing={2} sx={{ mt: 0 }}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small" sx={{ "& .MuiInputBase-root": { height: 35 } }}>
                      <InputLabel>Direction *</InputLabel>
                      <Select
                        value={newWebhook.direction}
                        onChange={(e) => setNewWebhook({
                          ...newWebhook,
                          direction: e.target.value,
                          method: e.target.value === 'inbound' ? 'POST' : newWebhook.method
                        })}
                        label="Direction *"
                        disabled={!!newWebhook.id || isPlayground}
                      >
                        <MenuItem value="outbound">Outbound (send data out)</MenuItem>
                        <MenuItem value="inbound">Inbound (receive data)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Webhook Name *"
                      value={newWebhook.name}
                      onChange={(e) => setNewWebhook({ ...newWebhook, name: e.target.value })}
                      placeholder="e.g., CRM Integration"
                      disabled={isPlayground}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label={newWebhook.direction === 'inbound' ? 'Webhook URL (auto-generated)' : 'Webhook URL *'}
                      value={newWebhook.url}
                      onChange={(e) => setNewWebhook({ ...newWebhook, url: e.target.value })}
                      disabled={newWebhook.direction === 'inbound' || (newWebhook.id && newWebhook.direction === 'inbound') || isPlayground}
                      placeholder="https://example.com/webhook"
                      InputProps={{
                        endAdornment: newWebhook.direction === 'inbound' && newWebhook.url ? (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => handleCopyToClipboard(newWebhook.url, 'Webhook URL')}
                              edge="end"
                            >
                              <ContentCopy />
                            </IconButton>
                          </InputAdornment>
                        ) : null,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small" sx={{ "& .MuiInputBase-root": { height: 35 } }}>
                      <InputLabel>Method *</InputLabel>
                      <Select
                        value={newWebhook.direction === 'inbound' ? 'POST' : newWebhook.method}
                        onChange={(e) => setNewWebhook({ ...newWebhook, method: e.target.value })}
                        label="Method *"
                        disabled={newWebhook.direction === 'inbound' || isPlayground}
                      >
                        <MenuItem value="GET">GET</MenuItem>
                        <MenuItem value="POST">POST</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {newWebhook.direction === 'inbound' && (
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small" sx={{ "& .MuiInputBase-root": { height: 35 } }}>
                        <InputLabel>Module *</InputLabel>
                        <Select
                          value={newWebhook.module}
                          onChange={(e) => setNewWebhook({ ...newWebhook, module: e.target.value, selected_fields: [] })}
                          label="Module *"
                          disabled={isPlayground}
                        >
                          {availableModules.map((m) => {
                            // Handle both string and object formats (defensive)
                            const moduleValue = typeof m === 'string' ? m : (m.name || m.id || String(m));
                            return (
                              <MenuItem key={moduleValue} value={moduleValue}>{moduleValue}</MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  {newWebhook.direction === 'inbound' && newWebhook.module && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, minHeight: 40, alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ mr: 2, fontWeight: 'bold' }}>Select Fields:</Typography>
                        {isFieldsLoading && (
                          <Typography variant="caption" color="text.secondary">Loading fields…</Typography>
                        )}
                        {Object.entries(availableFields).map(([fieldKey, fieldMeta]) => (
                          <MDButton
                            key={fieldKey}
                            variant={(newWebhook.selected_fields || []).includes(fieldKey) ? 'gradient' : 'outlined'}
                            color={(newWebhook.selected_fields || []).includes(fieldKey) ? 'info' : 'secondary'}
                            size="small"
                            onClick={() => {
                              if (!isPlayground) {
                                const current = newWebhook.selected_fields || [];
                                const exists = current.includes(fieldKey);
                                const next = exists ? current.filter((f) => f !== fieldKey) : [...current, fieldKey];
                                setNewWebhook({ ...newWebhook, selected_fields: next });
                              }
                            }}
                            disabled={isPlayground}
                          >
                            {fieldMeta?.label || fieldKey}
                          </MDButton>
                        ))}
                        {!isFieldsLoading && newWebhook.module && Object.keys(availableFields).length === 0 && (
                          <Typography variant="caption" color="text.secondary">No fields</Typography>
                        )}
                      </Box>
                      {/* JSON Template display */}
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          {(newWebhook.selected_fields || []).length > 0
                            ? "Example JSON payload to send:"
                            : "Select fields above to see JSON template"
                          }
                        </Typography>
                        {(newWebhook.selected_fields || []).length > 0 && (
                          <>
                            <Card sx={{ p: 1, mt: 1, backgroundColor: '#fafafa', position: 'relative' }}>
                              <MDButton
                                size="small"
                                variant="gradient"
                                color="info"
                                startIcon={<ContentCopy />}
                                onClick={() => {
                                  const obj = (newWebhook.selected_fields || []).reduce(
                                    (acc, key) => ({ ...acc, [key]: "<value>" }),
                                    {}
                                  );
                                  handleCopyToClipboard(JSON.stringify(obj, null, 2), 'JSON template');
                                }}
                                sx={{
                                  position: 'absolute',
                                  top: 8,
                                  right: 8,
                                  zIndex: 1,
                                }}
                              >
                                Copy JSON
                              </MDButton>

                              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {`${JSON.stringify(
                                  (newWebhook.selected_fields || []).reduce(
                                    (acc, key) => ({ ...acc, [key]: "<value>" }),
                                    {}
                                  ),
                                  null,
                                  2
                                )}`}
                              </pre>
                            </Card>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                            </Box>
                          </>
                        )}
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Description (Optional)"
                      value={newWebhook.description}
                      onChange={(e) => setNewWebhook({ ...newWebhook, description: e.target.value })}
                      placeholder="Describe what this webhook is for"
                      multiline
                      rows={2}
                      disabled={isPlayground}
                    />
                  </Grid>
                  {newWebhook.direction === 'inbound' && (
                    <Grid item xs={12}>
                      <MDButton
                        variant="gradient"
                        color="info"
                        startIcon={<Add />}
                        onClick={async () => {
                          if (isPlayground) return;
                          try {
                            setIsGeneratingInbound(true);
                            const res = await axiosInstance.post('/webhook/inbound/create', {
                              name: newWebhook.name,
                              module: newWebhook.module || undefined,
                              selected_fields: newWebhook.selected_fields || [],
                            });
                            const backendUrl = getBackendBaseUrl();
                            const fullUrl = res.data?.url || `${backendUrl}/webhook/inbound/${res.data?.token}`;
                            setNewWebhook({
                              ...newWebhook,
                              url: fullUrl,
                              method: 'POST',
                              token: res.data?.token || newWebhook.token,
                              module: newWebhook.module,
                              selected_fields: newWebhook.selected_fields,
                            });
                            setNotification({ open: true, message: 'Inbound URL generated', type: 'success' });
                          } catch (e) {
                            setNotification({ open: true, message: 'Failed to generate inbound URL', type: 'error' });
                          } finally {
                            setIsGeneratingInbound(false);
                          }
                        }}
                        sx={{ height: '40px' }}
                        disabled={isGeneratingInbound || !newWebhook.module || isPlayground}
                      >
                        {isGeneratingInbound ? 'Generating…' : 'Generate URL'}
                      </MDButton>
                    </Grid>
                  )}
                </Grid>
              </DialogContent>
              <DialogActions sx={{ px: 3, py: 2 }}>
                <Box flex={1} />
                <MDButton variant="outlined" color="secondary" onClick={() => setIsWebhookDialogOpen(false)} sx={{ mr: 1 }}>
                  Cancel
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="info"
                  startIcon={newWebhook.id ? <Edit /> : <Add />}
                  onClick={handleAddWebhook}
                  disabled={isDialogSaving || !newWebhook.name || (newWebhook.direction === 'outbound' && (!newWebhook.url || !newWebhook.method)) || isPlayground}
                >
                  {isDialogSaving ? (newWebhook.id ? 'Updating…' : 'Adding…') : (newWebhook.id ? 'Update Webhook' : 'Add Webhook')}
                </MDButton>
              </DialogActions>
            </Dialog>

            {/* Existing Webhooks */}
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                  <MDTypography variant="h6" fontWeight="bold" color="text">
                    Configured Webhooks
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary">
                    {webhooks.length === 0
                      ? 'No webhooks configured yet'
                      : `${webhooks.length} webhook${webhooks.length === 1 ? '' : 's'} configured`
                    }
                  </MDTypography>
                </Box>
                <MDButton
                  variant="gradient"
                  color="info"
                  startIcon={<Add />}
                  onClick={() => {
                    if (isPlayground) {
                      setNotification({
                        open: true,
                        message: "Cannot add webhooks in playground mode",
                        type: 'warning'
                      });
                      return;
                    }
                    setNewWebhook({
                      name: '',
                      url: '',
                      method: 'GET',
                      direction: 'outbound',
                      description: '',
                      isActive: true,
                      module: '',
                      selected_fields: [],
                      token: undefined,
                    });
                    setIsWebhookDialogOpen(true);
                  }}
                  disabled={isPlayground}
                >
                  Add Webhook
                </MDButton>
              </Box>

              {webhooks.length === 0 && !isPlayground ? (
                <Card sx={{
                  p: 4,
                  textAlign: 'center',
                  backgroundColor: '#f8f9fa',
                  border: '2px dashed #e0e0e0',
                  borderRadius: 2
                }}>
                  <Box sx={{ mb: 2 }}>
                    <Extension sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5 }} />
                  </Box>
                  <MDTypography variant="h6" color="text.secondary" gutterBottom>
                    No Webhooks Configured
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Create your first webhook to start integrating with external services
                  </MDTypography>
                  <MDButton
                    variant="gradient"
                    color="info"
                    startIcon={<Add />}
                    onClick={() => {
                      setNewWebhook({
                        name: '',
                        url: '',
                        method: 'GET',
                        direction: 'outbound',
                        description: '',
                        isActive: true,
                        module: '',
                        selected_fields: [],
                        token: undefined,
                      });
                      setIsWebhookDialogOpen(true);
                    }}
                    disabled={isPlayground}
                  >
                    Create First Webhook
                  </MDButton>
                </Card>
              ) : (
                <Grid container spacing={3}>
                  {webhooks.map((webhook) => (
                    <Grid item xs={12} key={webhook.id}>
                      <Card sx={{
                        p: 3,
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          transform: 'translateY(-2px)'
                        },
                        position: 'relative',
                        ...(isPlayground && { opacity: 0.9 })
                      }}>

                        {/* Header Section */}
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                          <Box flex={1}>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              <MDTypography variant="h6" fontWeight="bold" color="text">
                                {webhook.name}
                              </MDTypography>
                              <Box
                                sx={{
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 1,
                                  backgroundColor: webhook.direction === 'inbound' ? '#e3f2fd' : '#f3e5f5',
                                  color: webhook.direction === 'inbound' ? '#1565C8' : '#7b1fa2'
                                }}
                              >
                                <MDTypography variant="caption" fontWeight="medium">
                                  {webhook.direction === 'inbound' ? 'Inbound' : 'Outbound'}
                                </MDTypography>
                              </Box>
                              <Box
                                sx={{
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 1,
                                  backgroundColor: webhook.isActive ? '#e8f5e8' : '#ffebee',
                                  color: webhook.isActive ? '#2e7d32' : '#d32f2f'
                                }}
                              >
                                <MDTypography variant="caption" fontWeight="medium">
                                  {webhook.isActive ? 'Active' : 'Inactive'}
                                </MDTypography>
                              </Box>
                            </Box>
                            {webhook.description && (
                              <MDTypography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {webhook.description}
                              </MDTypography>
                            )}
                          </Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Tooltip title="Share webhook information" arrow>
                              <MDButton
                                variant="outlined"
                                color="info"
                                size="small"
                                startIcon={<Share />}
                                onClick={() => handleShareWebhook(webhook)}
                                sx={{ minWidth: 'auto' }}
                                disabled={isPlayground}
                              >
                                Share
                              </MDButton>
                            </Tooltip>
                            <Tooltip title={webhook.isActive ? "Deactivate webhook" : "Activate webhook"} arrow>
                              <MDButton
                                variant={webhook.isActive ? "outlined" : "gradient"}
                                color={webhook.isActive ? "error" : "success"}
                                size="small"
                                onClick={() => handleToggleWebhook(webhook.id)}
                                sx={{ minWidth: 'auto' }}
                                disabled={isPlayground}
                              >
                                {webhook.isActive ? 'Deactivate' : 'Activate'}
                              </MDButton>
                            </Tooltip>
                            <Tooltip title="Edit Webhook" arrow>
                              <IconButton
                                color="primary"
                                onClick={() => handleEditWebhook(webhook)}
                                disabled={isPlayground}
                                sx={{
                                  '&:hover': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.1)'
                                  }
                                }}
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Webhook" arrow>
                              <IconButton
                                color="error"
                                onClick={() => handleRemoveWebhook(webhook.id)}
                                disabled={isPlayground}
                                sx={{
                                  '&:hover': {
                                    backgroundColor: 'rgba(244, 67, 54, 0.1)'
                                  }
                                }}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>

                        {/* URL Section */}
                        <Box sx={{ mb: 2 }}>
                          <MDTypography variant="subtitle2" color="text.secondary" gutterBottom>
                            Webhook URL
                          </MDTypography>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 2,
                            backgroundColor: '#f8f9fa',
                            borderRadius: 1,
                            border: '1px solid #e0e0e0'
                          }}>
                            <MDTypography
                              variant="body2"
                              sx={{
                                wordBreak: 'break-all',
                                flex: 1,
                                fontFamily: FONT_MONO,
                                fontSize: '0.875rem'
                              }}
                            >
                              {webhook.url}
                            </MDTypography>
                            <Tooltip title="Copy webhook URL" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleCopyToClipboard(webhook.url, 'Webhook URL')}
                                sx={{
                                  color: 'primary.main',
                                  '&:hover': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.1)'
                                  }
                                }}
                              >
                                <ContentCopy fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>

                        {/* Details Section */}
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                          <Box display="flex" gap={3}>
                            <Box>
                              <MDTypography variant="caption" color="text.secondary">
                                Method
                              </MDTypography>
                              <MDTypography variant="body2" fontWeight="medium">
                                {webhook.method}
                              </MDTypography>
                            </Box>
                            {webhook.module && (
                              <Box>
                                <MDTypography variant="caption" color="text.secondary">
                                  Module
                                </MDTypography>
                                <MDTypography variant="body2" fontWeight="medium">
                                  {webhook.module}
                                </MDTypography>
                              </Box>
                            )}
                            {webhook.selected_fields && webhook.selected_fields.length > 0 && (
                              <Box>
                                <MDTypography variant="caption" color="text.secondary">
                                  Fields
                                </MDTypography>
                                <MDTypography variant="body2" fontWeight="medium">
                                  {webhook.selected_fields.length} selected
                                </MDTypography>
                              </Box>
                            )}
                          </Box>
                        </Box>

                        {/* Latest Payload Section for Inbound Webhooks */}
                        {webhook.direction === 'inbound' && webhook.token && latestByToken[webhook.token] && (
                          <Box sx={{
                            mt: 3,
                            p: 2,
                            backgroundColor: '#f8f9fa',
                            borderRadius: 1,
                            border: '1px solid #e0e0e0'
                          }}>
                            <MDTypography variant="subtitle2" color="text.secondary" gutterBottom>
                              Latest Received Data
                            </MDTypography>
                            <MDTypography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                              Received at: {latestByToken[webhook.token]?.received_at || '—'}
                            </MDTypography>

                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <MDTypography variant="subtitle2" gutterBottom>
                                  Payload
                                </MDTypography>
                                <Box sx={{
                                  p: 1.5,
                                  backgroundColor: 'white',
                                  borderRadius: 1,
                                  border: '1px solid #e0e0e0',
                                  maxHeight: 200,
                                  overflow: 'auto'
                                }}>
                                  <pre style={{
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    margin: 0,
                                    fontSize: '0.75rem',
                                    fontFamily: FONT_MONO
                                  }}>
                                    {JSON.stringify(latestByToken[webhook.token]?.payload || {}, null, 2)}
                                  </pre>
                                </Box>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <MDTypography variant="subtitle2" gutterBottom>
                                  Created Record
                                </MDTypography>
                                <Box sx={{
                                  p: 1.5,
                                  backgroundColor: 'white',
                                  borderRadius: 1,
                                  border: '1px solid #e0e0e0',
                                  maxHeight: 200,
                                  overflow: 'auto'
                                }}>
                                  <pre style={{
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    margin: 0,
                                    fontSize: '0.75rem',
                                    fontFamily: FONT_MONO
                                  }}>
                                    {JSON.stringify(latestByToken[webhook.token]?.created || {}, null, 2)}
                                  </pre>
                                </Box>
                              </Grid>
                            </Grid>
                          </Box>
                        )}

                        {isPlayground && (
                          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Lock fontSize="small" color="action" />
                            <MDTypography variant="caption" color="text.secondary">
                              Actions are disabled in playground mode
                            </MDTypography>
                          </Box>
                        )}
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Grid>
          </>
        )}

        {/* Save Button — API keys only; WhatsApp panel saves itself */}
        {!isPlayground && selectedSection === 'api' && (
          <Grid item xs={12} mt={3}>
            <Box display="flex" justifyContent="flex-end">
              <MDButton
                variant="gradient"
                color="info"
                onClick={onSave}
                disabled={isSaving}
                size="large"
              >
                {isSaving ? 'Saving...' : 'Save Settings'}
              </MDButton>
            </Box>
          </Grid>
        )}

        {/* Playground mode save button placeholder */}
        {isPlayground && selectedSection === 'api' && (
          <Grid item xs={12} mt={3}>
            <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
              <MDTypography variant="caption" color="text.secondary">
                <Lock fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                Saving is disabled in playground mode
              </MDTypography>
              <MDButton
                variant="gradient"
                color="info"
                disabled
                size="large"
              >
                Save Settings
              </MDButton>
            </Box>
          </Grid>
        )}
      </Grid>
    </MDBox>
  );
}

export default IntegrationsSettings;

